import React, {createContext, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getGuest} from "redux/actions/guestsActions";
import {setSelectedGuestBookings} from "redux/actions/bookingsActions";
import {orderBookings} from "utilities/helperFunctions";
// Styles & icons
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
// Components
import FlexContainer from "connectui/component/layout/FlexContainer";
import MessagesPanel from "components/Panels/MessagesPanel";
import Text from "connectui/component/display/Text";
import MessagesSection from "./MessagesSection";
import InputSection from "./InputSection";
import GuestHeaderSection from "./GuestHeaderSection";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {useFeatureFlags} from "utilities/featureUtil";
import Container from "../../../../../connectui/component/layout/Container";
import MobileInputSection from "./MobileInputSection";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      position: "relative",
    },
    iconBtn: {
      borderRadius: theme.spacing(2),
      backgroundColor: theme.colors.gray50,
      padding: "0px",
      border: "none",
      outline: "none",
      position: "absolute",
      top: theme.spacing(4),
      right: theme.spacing(4),
      zIndex: theme.zIndex.drawer,
      "&:hover": {backgroundColor: theme.colors.hoverGray},
      "&.open": {backgroundColor: theme.colors.gray200},
    },
    icon: {
      width: "28px",
      height: "28px",
      justifyContent: "center",
      padding: "5px",
      color: theme.colors.textSecondary,
      "&.open": {color: theme.colors.gray900},
    },
  });

const defaultContext = {
  tab: 0,
  setTab: () => null,
  replyEmailProps: {
    enabled: false,
    subject: "",
  },
  setReplyEmailProps: () => null,
  notes: [],
  setNotes: () => null,
};

export const MessagesContext = createContext(defaultContext);

const ConversationsPanel = ({
  sx = {},
  guestId,
  panelOpen,
  togglePanelOpen,
  goBack,
  ...props
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const {newInbox, inboxPanels} = useFeatureFlags();
  const guests_dict = useSelector((state) => state.defaultReducer.guests_dict);
  const bookings_dict = useSelector(
    (state) => state.defaultReducer.bookings_dict,
  );
  const guestBookings = useSelector(
    (state) => state.defaultReducer.selected_guest_bookings,
  );
  const messages = useSelector((state) => state.messagesReducer.messages);
  const loadingAPI = useSelector((state) => state.defaultReducer.loading);
  const [tab, setTab] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [notes, setNotes] = useState([]);
  const [replyEmailProps, setReplyEmailProps] = useState({
    enabled: false,
    subject: "",
  });
  const guest = guests_dict[guestId] ?? {};
  const lastUserMessage = useMemo(() => {
    return messages[guestId]?.messages
      ?.filter(
        (m) =>
          !m.hasOwnProperty("notification_id") &&
          !m.hasOwnProperty("notification_type") &&
          m.channel !== "note",
      )
      ?.slice(-1)?.[0];
  }, [messages, guestId]);
  const currentGuestId = useMemo(
    () => guestBookings[0]?.guest_id,
    [guestBookings],
  );

  useEffect(() => {
    if (
      guests_dict[guestId] &&
      !guests_dict[guestId].metadata &&
      !loadingAPI.guest
    ) {
      dispatch(getGuest(guestId));
    }
  }, [guestId, guests_dict, loadingAPI, dispatch]);

  useEffect(() => {
    if (!guestId || currentGuestId === guestId || (!isMobile && panelOpen)) {
      return;
    }

    if (guest?.guest_id && bookings_dict) {
      const newOrderedBkgs = orderBookings(guest, bookings_dict, guestId);
      dispatch(setSelectedGuestBookings(newOrderedBkgs));
    }
  }, [
    guestId,
    currentGuestId,
    guest?.guest_id,
    bookings_dict,
    isMobile,
    panelOpen,
    dispatch,
  ]);

  return (
    <MessagesContext.Provider
      value={{
        tab,
        setTab,
        replyEmailProps,
        setReplyEmailProps,
        notes,
        setNotes,
      }}
    >
      <FlexContainer
        className={classes.container}
        flexDirection="column"
        {...props}
      >
        {/* Header */}
        {inboxPanels && (
          <GuestHeaderSection
            guest={guest}
            goBack={goBack}
            panelOpen={panelOpen}
            loadingMessages={loadingMessages}
            togglePanelOpen={togglePanelOpen}
          />
        )}

        <FlexContainer
          flexDirection="column"
          className={classes.container}
          sx={{overflow: "hidden"}}
        >
          {/* Empty panel */}
          {!guestId ? (
            <FlexContainer
              className={classes.container}
              alignItems="center"
              justifyContent="center"
            >
              <Text
                variant="textLG"
                color="gray400"
                sx={{marginBottom: inboxPanels ? "60px" : 0}}
              >
                {"Select a guest to see messages"}
              </Text>
            </FlexContainer>
          ) : inboxPanels ? (
            <>
              {/* Conversations */}
              <MessagesSection
                loadingMessages={loadingMessages}
                setLoadingMessages={setLoadingMessages}
                guestId={guestId}
              />
              {tab === 0 && (
                <>
                  {/* Input */}
                  {isMobile ? (
                    <MobileInputSection
                      guestId={guestId}
                      lastMessage={lastUserMessage}
                      skeleton={{enabled: loadingMessages}}
                    />
                  ) : (
                    <InputSection
                      guestId={guestId}
                      guestPanelClosed={!panelOpen}
                      lastMessage={lastUserMessage}
                      skeleton={{enabled: loadingMessages}}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            // Old Conversations panel
            <>
              {!!newInbox && (
                <ButtonIcon
                  icon={
                    <SvgIcon
                      component={ICONS["layout-sidebar-right"]}
                      className={[classes.icon, panelOpen ? "open" : ""].join(
                        " ",
                      )}
                    />
                  }
                  className={[classes.iconBtn, panelOpen ? "open" : ""].join(
                    " ",
                  )}
                  onClick={togglePanelOpen}
                />
              )}
              <Container width="100%" height="100%">
                <MessagesPanel
                  guestId={guestId}
                  initialGuestMsgs={messages[guestId]?.messages || []}
                />
              </Container>
            </>
          )}
        </FlexContainer>
      </FlexContainer>
    </MessagesContext.Provider>
  );
};

export default ConversationsPanel;
